import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  CircularProgress
} from '@mui/material';
import SearchNotFound from 'components/SearchNotFound';

interface TableColumn {
  field: string;
  headerName: string;
  flex?: number;
  minWidth?: number;
  renderCell?: (params: any) => React.ReactNode;
  renderHeader?: () => React.ReactNode;
}

interface TableProps {
  rows: Array<any>;
  columns: TableColumn[];
  loading?: boolean;
  pageSize?: number;
  totalRecords?: number;
  currentPage?: number; // 0-based page number
  onEdit?: (id: number) => void;
  onDelete?: (id: number) => void;
  onRowClick?: (row: any) => void;
  handlePageChange?: (page: number) => void;
  handlePageSizeChange?: (pageSize: number) => void;
  showViewSitesButton?: boolean;
  onSiteButtonClick?: (row: any) => void;
  noAction?: boolean;
  customActions?: (row: any) => React.ReactNode; // New prop for custom actions
}

const CommonTable: React.FC<TableProps> = ({
  rows,
  columns,
  loading,
  pageSize = 10,
  totalRecords = 0,
  currentPage: currentPageProp,
  onEdit,
  onDelete,
  onRowClick,
  handlePageChange,
  handlePageSizeChange,
  showViewSitesButton = false,
  onSiteButtonClick,
  noAction,
  customActions // New prop
}) => {
  const [currentPage, setCurrentPage] = useState(0);

  // Sync internal state with prop when prop changes
  useEffect(() => {
    if (currentPageProp !== undefined) {
      setCurrentPage(currentPageProp);
    }
  }, [currentPageProp]);

  const handlePageChangeInternal = (_: unknown, newPage: number) => {
    setCurrentPage(newPage);
    handlePageChange && handlePageChange(newPage);
  };

  const handlePageSizeChangeInternal = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newSize = parseInt(event.target.value, 10);
    handlePageSizeChange && handlePageSizeChange(newSize);
  };

  const handleRowClickInternal = (row: any) => {
    onRowClick && onRowClick({ row });
  };

  return (
    <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '300px'
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {columns.map((col) => (
                    <TableCell
                      key={col.field}
                      sx={{
                        minWidth: col.minWidth,
                        flex: col.flex || 1,
                        fontWeight: 'bold'
                      }}
                    >
                      {col.renderHeader ? col.renderHeader() : col.headerName}
                    </TableCell>
                  ))}
                  {!noAction ? <TableCell>Actions</TableCell> : ''}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.length > 0 ? (
                  rows.map((row) => (
                    <TableRow
                      key={row.Id}
                      hover
                      onClick={() => handleRowClickInternal(row)}
                      sx={{ cursor: 'pointer' }}
                    >
                      {columns.map((col) => (
                        <TableCell key={col.field}>
                          {col.renderCell
                            ? col.renderCell(row[col.field])
                            : row[col.field] === 0
                            ? 0
                            : row[col.field] || 'N/A'}
                        </TableCell>
                      ))}
                      {!noAction ? (
                        <TableCell>
                          <Box display="flex" gap={1}>
                            {customActions ? (
                              customActions(row) // Render custom actions if provided
                            ) : (
                              <>
                                <Button
                                  variant="outlined"
                                  color="primary"
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onEdit && onEdit(row.Id);
                                  }}
                                >
                                  Edit
                                </Button>
                                {showViewSitesButton && (
                                  <Button
                                    variant="outlined"
                                    color="secondary"
                                    size="small"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      onSiteButtonClick && onSiteButtonClick(row);
                                    }}
                                  >
                                    View Sites
                                  </Button>
                                )}
                                <Button
                                  variant="outlined"
                                  color="error"
                                  size="small"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    onDelete && onDelete(row.Id);
                                  }}
                                >
                                  Delete
                                </Button>
                              </>
                            )}
                          </Box>
                        </TableCell>
                      ) : (
                        ''
                      )}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell align="center" colSpan={columns.length + 1}>
                      <SearchNotFound searchQuery="" />{' '}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            rowsPerPageOptions={[5, 10, 20, 50]}
            component="div"
            count={totalRecords}
            rowsPerPage={pageSize}
            page={currentPage}
            onPageChange={handlePageChangeInternal}
            onRowsPerPageChange={handlePageSizeChangeInternal}
          />
        </>
      )}
    </Box>
  );
};

export default CommonTable;
